import { useCallback, useEffect, useState, type ReactNode } from "react";

import { services, type Transport } from "@tdelivr/api-client";
import type { PlatformSchemaSummary } from "@tdelivr/api-client/generated/event-platform/event-schema-registry.js";

import { boundedNote, PER_FETCH } from "../board.ts";
import {
  EVENT_SCHEMAS_SCOPE,
  REGISTER_REFUSAL,
  schemaRow,
  stateOf,
  statesPresent,
  versionSpanNote,
} from "../event-schemas.ts";
import type { Session } from "../session.ts";
import { stateForError, type ViewState } from "../view-state.ts";
import { RecordsBoard } from "./RecordsBoard.tsx";

/**
 * `schemas` in `16 WP15 Platform Machinery.dc.html`: every event contract the platform has
 * registered.
 *
 * ```
 * schemas:['Event schema registry','EVT-01','Published contracts are immutable. A change is a new
 *   version; the old one stays readable forever.',
 *   [['Schemas','318','done','published'],['Drafts','4','idle','unpublished'],
 *    ['Breaking changes blocked','1','failed','this week'],['Consumers','96','done','subscribed']],
 *   ['Schema','Version','Compatibility'],
 *   [['payments.settled','v2','backward','lock','immutable'], …],
 *   'lock','Published rows show a lock; there is no edit button on a published schema.']
 * ```
 *
 * What is drawn from data and what is not:
 *
 * | drawn                                   | source                                              |
 * | --------------------------------------- | --------------------------------------------------- |
 * | the rows                                | `EVT-01 GET /v1/platform/event-schemas` (ADR 0057)  |
 * | `Schema`                                | `schemas.subject`                                   |
 * | `Version` — `v2`                        | `schemas.version`                                   |
 * | `Compatibility` — `backward`, `full`    | `schemas.compatibilityPolicy`                       |
 * | `lock · immutable`                      | the record's own promise — immutable by trigger, so no field. Drawn `closed`: Q-193 |
 * | the detail panel's owner, type, digest  | `ownerServiceId`, `eventType`, `digest`             |
 * | `Created`                               | `schemas.registeredAt`, without the design's name beside it. Q-107 |
 * | `Last changed`                          | **nothing** — the row cannot change, so there is no second instant. Q-107 |
 * | `v5 draft`, `breaking`, `blocked: breaking` | **nothing** — a refused registration leaves no row. Q-200 |
 * | the four KPI tiles                      | **nothing** — R-04 for the count, Q-200 for the other three, Q-178 for the row |
 * | `Register version`                      | **nothing** — the registration is `/internal/v1`. `REGISTER_REFUSAL` |
 *
 * **This page needed the listing itself, not a tenancy change**, which is what makes it the odd one
 * of the five ADR 0048 produced. The other four each had a reachable, public, parameterless listing
 * answering the wrong rows — one tenant's — and the fix was a second operation with the tenant
 * predicate omitted. EVT-01 had **no listing at all**: three operations under `/internal/v1`, of
 * which the by-version read needs the `subject` and `version` a board exists to discover. And it has
 * no tenant to cross: `service.yaml` declares `tenant_aware: false` and no registry table has a
 * `tenant_id`, so what crosses a boundary here is the row rather than the listing — which is why
 * ADR 0057 §4 makes the **projection** structural where ADR 0039 §4 made the tenancy bound
 * structural.
 *
 * All three of the design's columns have a published source, which no other board on this console
 * can say. Every absence it still carries is a row, a tile or a line of the `History` block, and
 * `../event-schemas.ts` is where each is argued. Nothing about any of them is decided here.
 */

const registry = services.event_platform__event_schema_registry;

export interface EventSchemasProps {
  readonly transport: Transport;
  readonly session: Session;
  /** Innermost last: `Platform › Machinery › Event schema registry`, the design's own trail. */
  readonly crumbs: readonly string[];
}

/** The design's `PAGES` row, so the words on screen and the words here cannot drift apart. */
const TITLE = "Event schema registry";
const SOURCE = "EVT-01";
const DESCRIPTION =
  "Published contracts are immutable. A change is a new version; the old one stays readable forever.";
const COLUMNS = ["Schema", "Version", "Compatibility"] as const;
const NOTE = "Published rows show a lock; there is no edit button on a published schema.";
/** The design's own `noteK` for this page is `lock`, which its `NK` map draws in the accent tint. */
const NOTE_KIND = "lock" as const;
/** The design's `FORMS.schemas` primary button, word for word. */
const PRIMARY_LABEL = "Register version";

export function EventSchemas({ transport, session, crumbs }: EventSchemasProps): ReactNode {
  const [rows, setRows] = useState<readonly PlatformSchemaSummary[] | undefined>(undefined);
  const [truncated, setTruncated] = useState(false);
  const [failure, setFailure] = useState<ViewState | undefined>(undefined);
  const [state, setState] = useState("");

  const load = useCallback(async () => {
    setRows(undefined);
    setFailure(undefined);

    /*
     * Asked for before the call, not discovered from the refusal.
     *
     * The board renders the console's own forbidden state naming the scope, which is what an
     * operator can act on — they can ask their administrator for `event-schema:platform:read` by
     * name. A 403 from the service renders the same state, so this is not the only guard; it is the
     * one that makes the scope a literal in this application's source, which is what
     * `channel-scopes.mjs` derives the Keycloak grant from. ADR 0049 §3, and the notification
     * bell's three-console 403 is what happens without it.
     */
    if (!session.scopes.has(EVENT_SCHEMAS_SCOPE)) {
      setFailure({ kind: "forbidden", missingScopes: [EVENT_SCHEMAS_SCOPE] });
      return;
    }

    try {
      const client = registry.createEventSchemaRegistryClient(transport);
      /*
       * `listPlatformEventSchemas`, which is the only operation of this service a browser can reach.
       *
       * The three it sits beside are the workload surface and stay exactly where they are: the
       * registration and the compatibility check are commands, and `getEventSchema` answers a whole
       * document — including the `sensitive` flag on every property — under `event-schema:read`,
       * which ADR 0057 §2 refuses to widen for the sake of a board drawing three columns.
       *
       * `after` is deliberately not sent. One request of `limit` rows is the board, and
       * `boundedNote` says so when the answer was full rather than leaving an operator to assume
       * they have seen every contract on the platform. The cursor is `subject:version` and its two
       * halves are immutable by trigger, so a second page could be assembled correctly — ADR 0057
       * §5 — but a board that pages the service cannot count what it is not holding, and the
       * grid's own pager pages what it holds.
       */
      const page = await client.listPlatformEventSchemas(PER_FETCH);
      setRows(page.schemas);
      setTruncated(page.nextAfter !== null);
    } catch (error) {
      setFailure(stateForError(error, [EVENT_SCHEMAS_SCOPE]));
    }
  }, [transport, session.scopes]);

  useEffect(() => {
    void load();
  }, [load]);

  /*
   * Filtered by the same function that badges the row, and the predicate takes no row.
   *
   * Every row of this registry is in the same state and `stateOf` is why — it reads nothing,
   * because the immutability is the record's rather than a field's. Written through `stateOf`
   * anyway, rather than compared against the literal `"immutable"`, so that a board and its filter
   * cannot come to hold two opinions about what a row's state is.
   */
  const boardRows =
    rows === undefined
      ? undefined
      : rows.filter(() => state === "" || state === stateOf().label).map(schemaRow);

  const bounded = boundedNote(
    rows?.length,
    truncated,
    "schema versions",
    "ordered by subject and then by version",
  );
  const span = versionSpanNote(rows);

  return (
    <RecordsBoard
      crumbs={crumbs}
      title={TITLE}
      source={SOURCE}
      description={DESCRIPTION}
      columns={COLUMNS}
      rows={boardRows}
      {...(failure === undefined ? {} : { failure })}
      onRetry={() => {
        void load();
      }}
      purpose="No event contract has been registered yet. A row appears here when a service registers a schema version, and it stays readable forever afterwards."
      primary={{ label: PRIMARY_LABEL, refusal: REGISTER_REFUSAL }}
      filter={{
        // The design's own word for unfiltered, from its `gridUI`: `All states`.
        unfiltered: "All states",
        options: (rows === undefined ? [] : statesPresent(rows)).map((label) => ({
          value: label,
          label,
        })),
        value: state,
        onChange: setState,
      }}
      note={{ kind: NOTE_KIND, glyph: "lock", text: NOTE }}
      {...(bounded === undefined ? {} : { bounded })}
      {...(span === undefined ? {} : { footnote: span })}
      exportName="event-schemas"
      detailSubtitle={`${TITLE} · ${SOURCE}`}
      detailNote={NOTE}
    />
  );
}
